import { InputAdornment, Stack as MuiStack, TextField, Typography } from "@mui/material";
import { SearchIcon } from "src/icons/SearchIcon";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { DeviceCategory, DeviceItem, GetDevicesOfProjectResponse } from "src/model/api/v2/data-contracts";
import { DeviceAccordionView } from "src/pages/PlannerPage/DeviceView/DeviceAccordionView";
import { ts } from 'src/utils/translate';
import { DeviceViewCabinets } from 'src/pages/PlannerPage/DeviceView/DeviceViewCabinets';
import { DeviceViewMyProducts } from "src/pages/PlannerPage/DeviceView/DeviceViewMyProducts";
import {
    CustomItemDrop,
    NoteAddToDevice,
    NoteDrop,
    NoteEdit,
    NoteEditOnDevice
} from "src/pages/PlannerPage/PlannerPage";
import { filterDevices, flattenDevices, reconstructDeviceTree } from 'src/pages/PlannerPage/utils/device-tree-utils';
import { useAppSelector } from 'src/app/hooks';
import { endMultiPlacement } from 'src/pages/PlannerPage/DeviceView/hooks/DragAndDrop/usePlannerDrag';

const DEVICE_SEARCH_DEBOUNCE_MS = 300;

export const DeviceView = (props: {
    getDevices: GetDevicesOfProjectResponse,
    customItemDrop: CustomItemDrop | null,
    setCustomItemDrop: (drop: CustomItemDrop | null) => void,
    noteDrop: NoteDrop | null,
    setNoteDrop: (drop: NoteDrop | null) => void,
    noteEdit: NoteEdit | null,
    setNoteEdit: (edit: NoteEdit | null) => void,
    noteEditOnDevice: NoteEditOnDevice | null,
    setNoteEditOnDevice: (edit: NoteEditOnDevice | null) => void,
    noteAddToDevice: NoteAddToDevice | null,
    setNoteAddToDevice: (add: NoteAddToDevice | null) => void,
}) => {

    const selectedDevice = useAppSelector(s => s.plannerUi.selectedDevice);

    const [filterText, setFilterText] = useState("");
    const [debouncedFilterText, setDebouncedFilterText] = useState("");

    useEffect(() => {
        return () => endMultiPlacement();
    }, []);

    useEffect(() => {
        const timeout = setTimeout(
            () => setDebouncedFilterText(filterText),
            DEVICE_SEARCH_DEBOUNCE_MS
        );
        return () => clearTimeout(timeout);
    }, [filterText]);

    const allDevices: DeviceItem[] = useMemo(() =>
        flattenDevices(props.getDevices.categories), [props.getDevices.categories]);

    const filteredDevices: DeviceItem[] = useMemo(
        () => filterDevices(debouncedFilterText, allDevices),
        [debouncedFilterText, allDevices]
    );

    const filteredTree: DeviceCategory[] = useMemo(() =>
            reconstructDeviceTree(props.getDevices.categories, filteredDevices),
        [filteredDevices, props.getDevices.categories]
    );

    const handleFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
        setFilterText(event.target.value);
    };

    if (props.getDevices === null) {
        return <></>;
    }

    return (
        <>
            <MuiStack
                direction="column"
                spacing={1.5}
                sx={{pt: 1}}
            >
                <TextField
                    placeholder={ts('planner_device_search', 'Search for devices...')}
                    variant="outlined"
                    onChange={handleFilterChange}
                    sx={{
                        boxShadow: 'none',
                        '& .MuiOutlinedInput-root': {
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none',
                            },
                            '&.Mui-focused': {
                                boxShadow: 'none',
                            },
                        },
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon/>
                            </InputAdornment>
                        ),
                      },
                    }}
                />

                {filteredDevices.length === 0 ? (
                    <Typography>No devices found.</Typography> //there's no figma design for this
                ) : (
                    <DeviceAccordionView
                        categories={filteredTree}
                        selectedDevice={selectedDevice}
                        autoExpandAll={debouncedFilterText.trim() !== ''}
                    />
                )}
                <DeviceViewCabinets
                    selectedDevice={selectedDevice}
                />
                <DeviceViewMyProducts
                    customItemDrop={props.customItemDrop}
                    setCustomItemDrop={props.setCustomItemDrop}
                    noteDrop={props.noteDrop}
                    setNoteDrop={props.setNoteDrop}
                    noteEdit={props.noteEdit}
                    setNoteEdit={props.setNoteEdit}
                    noteEditOnDevice={props.noteEditOnDevice}
                    setNoteEditOnDevice={props.setNoteEditOnDevice}
                    noteAddToDevice={props.noteAddToDevice}
                    setNoteAddToDevice={props.setNoteAddToDevice}
                    selectedDevice={selectedDevice}
                />
            </MuiStack>
        </>
    )
}